import { Button, Col, FormInstance, Row, Spin, Table, message } from "antd";
import React, { RefObject } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import {
  APIError,
  Category,
  Department,
  FormFieldConfig,
  SubCategory,
} from "../helpers/shared-interfaces";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  convertSlaToDays,
  generateDisplayErrorMessage,
  handleValidationErrors,
  injectKey,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { AxiosResponse } from "axios";
import {
  DELETE_SUBCATEGORY,
  GET_CATEGORY,
  UPDATE_CATEGORY,
} from "../gql/categories";
import { ColumnType, ColumnsType } from "antd/lib/table";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import { GET_DEPARTMENTS } from "../gql/departments";

interface RouteParams {
  id: string;
}

interface State {
  id: string;
  name: string;
  isLoading: boolean;
  isActive: boolean;
  departments: Department[];
  department?: Department;
  category?: Category;
  subCategories?: SubCategory[];
}

export function getSubCategoriesColumns<T extends SubCategory>(
  deleteSubcategory: (id?: string) => void,
): ColumnType<T>[] {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render(text: string, record: SubCategory): JSX.Element {
        return (
          <NavLink to={`/subCategories/${record?.id}`}>{record?.name}</NavLink>
        );
      },
    },
    {
      title: "SLA (Days)",
      dataIndex: "sla",
      key: "sla",
      render(text: string, record: SubCategory): JSX.Element {
        return <span>{convertSlaToDays(record.sla)}</span>;
      },
    },
    {
      title: "Is Active?",
      dataIndex: "isActive",
      key: "isActive",
      render(text: string): JSX.Element {
        return <span>{text.toString()}</span>;
      },
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      width: "8%",
      render(text: string, record: SubCategory): JSX.Element {
        return (
          <NavLink
            to={{
              pathname: `/subCategories/${record.id}/edit`,
              state: { record },
            }}
          >
            <EditOutlined />
          </NavLink>
        );
      },
    },
    {
      title: "Delete",
      dataIndex: "Delete",
      key: "Delete",
      width: "8%",
      render(text: string, record: SubCategory): JSX.Element {
        return (
          <Button onClick={() => deleteSubcategory(record?.id)}>
            <DeleteOutlined />
          </Button>
        );
      },
    },
  ];
}

export function SubCategoriesTableFactory(
  data: SubCategory[],
  columns: ColumnsType<SubCategory>,
): JSX.Element {
  return (
    <Table
      bordered={true}
      columns={columns}
      dataSource={injectKey<SubCategory>(data, "id")}
    />
  );
}

export default class EditCategory extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      id: "",
      name: "",
      isLoading: true,
      isActive: false,
      departments: [],
      department: undefined,
    };
    this.reference = React.createRef<FormInstance>();
    this.getCategory = this.getCategory.bind(this);
    this.updateCategory = this.updateCategory.bind(this);
    this.deleteSubcategory = this.deleteSubcategory.bind(this);
    this.getAllDepartments = this.getAllDepartments.bind(this);
    this.redirectToNewSubCategoryPage =
      this.redirectToNewSubCategoryPage.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.setState({ id: this.props.match.params.id }, () => {
      this.getCategory();
      this.getAllDepartments();
    });
  }

  redirectToNewSubCategoryPage = (): void => {
    const { id } = this.state;
    this.props.history.push(`/subCategories/new?id=${id}`);
  };

  getCategory = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_CATEGORY,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const category = data.data.category;
            this.setState({
              category,
              name: category?.name,
              subCategories: category?.subCategories,
              department: category?.department,
              isActive: category?.isActive,
            });
          } else {
            message.error("Failed to get category data");
          }
        });
      },
    );
  };

  deleteSubcategory(id?: string): void {
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_SUBCATEGORY,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false });
            if (!data.errors && data.data.deleteSubCategory.result) {
              message.success("Subcategory deleted successfuly");
              this.getCategory();
            } else {
              message.error("Failed to delete subCategory");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false });
          });
      },
    );
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  changeIsActive = (checkedValue: boolean) => {
    this.setState({
      isActive: checkedValue,
    });
  };

  redirectToViewCategoryPage(): void {
    this.props.history.push(`/categories/${this.state.id}`);
  }

  getAllDepartments = (): void => {
    request({
      query: GET_DEPARTMENTS,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        departments: data.departments,
        isLoading: false,
      });
    });
  };

  async updateCategory(): Promise<void> {
    const { id, isActive } = this.state;
    interface Response {
      data?: {
        updateCategory: {
          category: Category;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "name",
        "departmentId",
        "isActive",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_CATEGORY,
        variables: {
          categoryId: id,
          ...data,
          isActive,
        },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateCategory",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Category updated sucessfully");
        this.redirectToViewCategoryPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to update category - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      departments,
      department,
      category,
      isActive,
      subCategories,
    } = this.state;
    const fields: FormFieldConfig[] = [
      {
        id: "name",
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        initialValue: category?.name,
        placeholder: "Name",
        colConfig: {
          span: 7,
        },
      },
      {
        name: "departmentId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Department",
        placeholder: "Department",
        initialValue: department?.id,
        selectOptions: departments,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "isActive",
        name: "isActive",
        rule: "boolean",
        inputType: "checkbox",
        required: false,
        label: "Is Active?",
        checkboxCheckedValue: isActive ? true : false,
        checkboxChangeHandler: (e) => this.changeIsActive(e.target.checked),
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "updateCategory",
        name: "updateCategoryButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateCategory,
      },
    ];

    return (
      <Card header="UPDATE CATEGORY">
        <>
          <div>
            <Spin spinning={isLoading}>
              <BaseForm
                layout="vertical"
                reference={this.reference}
                fieldsToHide={[]}
                fields={fields}
              />
            </Spin>
          </div>
          <div>
            <Row>
              <Col>
                <p
                  style={{
                    marginTop: "20px",
                    fontSize: "15px",
                  }}
                >
                  <b>Subcategories</b>
                </p>
              </Col>
              <Col span={20} style={{ textAlign: "right" }}>
                <EntitledSection
                  entitlement={entitlements.TransferRequests.create}
                >
                  <Button
                    className="base-button"
                    size="large"
                    data-testid="create-subCategory-button"
                    onClick={this.redirectToNewSubCategoryPage}
                  >
                    <PlusOutlined />
                    Add Subcategory
                  </Button>
                </EntitledSection>
              </Col>
            </Row>

            {subCategories && (
              <Table
                scroll={{ x: 400 }}
                columns={getSubCategoriesColumns(this.deleteSubcategory)}
                dataSource={subCategories}
              />
            )}
          </div>
        </>
      </Card>
    );
  }
}
